import * as _ from "lodash"
import { EvalFunction, parse } from "mathjs";

import {
    IdsMetric,
    IdsRow,
    IMetricDataSet, MetricAxisType, MetricRowAggType
} from "./PA.MetricDataSet.IF";
import { DataSetUtils } from "./DataSetUtils";

/** Use these options to define how the ComputedMetric instance will calculate metrics */
export interface IComputedMetricCreateOptions {
    // Required to for calculation ordering
    /** List all metrics required to calculate this metric */
    requiredMetricsIds?: string[]
    /** List all attributes required to calculate this metric */
    requiredAttributeIds?: string[]

    // For Formula Driven Computed Metrics
    // this will create a computed metric using this metricFormula if it's provided. will also define setCOmputedValuesAllRows using this formula
    //  if it's not set, then you must define getComputedValue and *either* setComputedValuesAllRows or setComputedValuesFilteredRows

    /** Used to create a computed metric, when provided. If provided, the ComputedMetric class will define a setComputedValuesAllRows method using this formula
     * If it's not set, then you must define the getComputedValue method and *either* setComputedValuesAllRows *or* setComputedValuesFilteredRows, not both. Think of this as a completely manual metric */
    metricFormula?: string
    /** This value will be used when Metrics or Attributes not available in dataset */
    unresolvedValue?: any

    // For Completely Manual Computed Metrics
    //getComputedValue?: (dataSet: IMetricDataSet, row: IdsRow) => any
    //setComputedValuesAllRows?: (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  allRows: IdsRow[])=> Promise<void>
    //setComputedValuesFilteredRows?: (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[], filteredRows: IdsRow[], allRows: IdsRow[])=> Promise<void>
}

export class ComputedMetric
    implements IdsMetric
{
    private _options: IComputedMetricCreateOptions
    private _Id: string
    private _Name: string
    private _ShortName: string
    private _SortOrder: number
    private _Hidden: boolean
    private _Format: string
    private _Currency: boolean
    private _AxisType: MetricAxisType
    readonly _rowAggType?: MetricRowAggType
    readonly _rowAggWeightMetricId?: string
    private _storeKey: string
    private _metricFormula: string
    private _metricFunction: EvalFunction
    private _errorCount: number
    private _getComputedValue: (dataSet: IMetricDataSet, row: IdsRow) => any
    private _setComputedValuesAllRows: (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  allRows: IdsRow[]) => Promise<void>
    private _setComputedValuesFilteredRows: (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  filteredRows: IdsRow[], allRows: IdsRow[]) => Promise<void>
    private _requiredMetricsIds: string[]
    private _requiredAttributeIds: string[]
    private _aggregateExpression: string

    constructor(settings: IdsMetric, options: IComputedMetricCreateOptions){
        this._options = options || {};
        this._Id = settings.Id
        this._Name = settings.Name
        this._ShortName = settings.ShortName
        this._SortOrder = settings.SortOrder
        this._Hidden = !!settings.Hidden
        this._Format = settings.format
        this._Currency = !!settings.currency
        this._AxisType = settings.axisType
        this._rowAggType = settings.rowAggType
        this._rowAggWeightMetricId = settings.rowAggWeightMetricId
        this._errorCount = 0
        this._storeKey = `computedMtr_${this._Id}`
        this._aggregateExpression = settings.aggregateExpression

        // inspect options and set computed metric properties
        this.setupComputedMetric()
    }

    get Id(): string { return this._Id }
    get Name(): string { return this._Name }
    get ShortName(): string { return this._ShortName }
    get SortOrder(): number { return this._SortOrder }
    get Hidden(): boolean { return this._Hidden }

    get axisType(): MetricAxisType { return this._AxisType }
    get aggregateExpression(): string { return this._aggregateExpression }
    get rowAggType(): MetricRowAggType { return this._rowAggType }
    get rowAggWeightMetricId(): string { return this._rowAggWeightMetricId }
    get format(): string { return this._Format; }
    get currency(): boolean { return this._Currency}

    get bMetricComputed(): boolean { return true}
    getComputedValue(dataSet: IMetricDataSet, row: IdsRow): any {
        return this._getComputedValue(dataSet, row);
    }

    // detailed docs are on IdsMetric interface
    get setComputedValuesAllRows(): (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  rows: IdsRow[]) => Promise<void> {
        // when this class is instantiated, the below property or the flitered one will be defined, not both. this class's constructor decides which one
        return this._setComputedValuesAllRows
    }
    // detailed docs are on IdsMetric interface
    get setComputedValuesFilteredRows(): (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  filteredRows: IdsRow[], allRows: IdsRow[]) => Promise<void> {
        return this._setComputedValuesFilteredRows
    }

    get requiredAttributeIds(): string[] { return this._requiredAttributeIds }
    get requiredMetricsIds(): string[] { return this._requiredMetricsIds }


    /** If this is a expression metric, it will define the required functions for your
     * If it's not, then you must provide the getComputedValue and either the setComputedValuesAllRows or the setComputedValuesFilteredRows functions */
    private setupComputedMetric(): void {
        const self = this
        let opts = this._options

        this._getComputedValue = (dataSet: IMetricDataSet, row: IdsRow) => {
            if (_.isEmpty(dataSet))
                return undefined;
            return dataSet.getRowData(row, self._storeKey);
        };


        // Check if there are any references to Visual Functions,
        //   if so, apply to filter scope
        let bApplyToFilterScope = false


        // Setup Evaluation Function
        self._metricFormula = opts?.metricFormula || ''
        let dctSymbols: {[id:string]: string} = {}
        let dctFnc: {[id:string]: string} = {}
        let sError: string[] = []

        let parsedFormula = parse(this._metricFormula)
        DataSetUtils.Formula_setDependentParts(parsedFormula, dctSymbols, dctFnc, sError)
        let expFuncCount = 0
        _.forEach(_.values(dctFnc), (fncName) => {
            let spc = DataSetUtils.Function_getSpecs(fncName)
            if (spc)
                expFuncCount++;
            if (spc && spc.bFilterScope) {
                bApplyToFilterScope = true
            }
        })

        self._requiredMetricsIds = opts.requiredMetricsIds || []
        self._requiredAttributeIds = opts.requiredAttributeIds || []

        // If not in existing require arrays, add to attributes as default
        _.forEach(_.values(dctSymbols), (aSymbolName) => {
            if(_.includes(self._requiredMetricsIds, aSymbolName)
              || _.includes(self._requiredAttributeIds, aSymbolName))
                return
            self._requiredAttributeIds.push(aSymbolName)
        })
        self._metricFunction = parsedFormula.compile()

        this._setComputedValuesAllRows = undefined
        this._setComputedValuesFilteredRows = undefined

        async function ComputeValues(dataSet: IMetricDataSet,
                                     rowAttributeIds: string[],
                                     rowMetricIds: string[],
                                     filterRows: IdsRow[],
                                     allRows: IdsRow[]): Promise<void> {
            if (_.isEmpty(dataSet))
                return;

            let applyRows = allRows;
            if (bApplyToFilterScope)
                applyRows = filterRows;

            const missingAttr = _.difference(self.requiredAttributeIds, rowAttributeIds);
            let missingMtrc = _.difference(self.requiredMetricsIds, rowMetricIds);
            if (missingAttr.length || missingMtrc.length) {
                // Set to default value ?
                if (!_.isUndefined(opts.unresolvedValue)) {
                    _.forEach(applyRows, (row) => {
                        dataSet.setRowData(row, self._storeKey, opts.unresolvedValue);
                    });
                }
                return;
            }

            let calcCtx: {row: IdsRow, [id:string]: any} = {row: undefined}
            let fncScope: any = {}
            if(expFuncCount){
                fncScope.VisualAvg = (metricId: string, wghtMetricId: string, bPositiveOnly?: boolean): number => {
                    let fncKey = `VizAvg-${metricId || '-'}-${wghtMetricId || '-'}`;
                    if(fncKey in calcCtx)
                        return dataSet.getRowData(calcCtx.row, fncKey);
                    // calc avg and set key
                    let fopts: any = {positiveOnly: !!bPositiveOnly};
                    if(!_.isEmpty(wghtMetricId)){
                        fopts.weightByMetricId = wghtMetricId
                        fopts.divZeroDefault = 0
                        fopts.fallBackToNoWeight = true
                    }
                    DataSetUtils.Metric_setAvg(dataSet, metricId, fncKey, applyRows, fopts)
                    calcCtx[fncKey] = true
                    return dataSet.getRowData(calcCtx.row, fncKey);
                }
                fncScope.VisualMin = (metricId: string, bPositiveOnly?: boolean): number => {
                    let fncKey = `VizMin-${metricId || '-'}`;
                    if(fncKey in calcCtx)
                        return dataSet.getRowData(calcCtx.row, fncKey);
                    // calc min and set key
                    let fopts: any = {bPositiveOnly: !!bPositiveOnly};
                    DataSetUtils.Metric_setMin(dataSet, metricId, fncKey, applyRows, fopts)
                    calcCtx[fncKey] = true
                    return dataSet.getRowData(calcCtx.row, fncKey);
                }
                fncScope.VisualMax = (metricId: string, bPositiveOnly?: boolean): number => {
                    let fncKey = `VizMax-${metricId || '-'}`;
                    if(fncKey in calcCtx)
                        return dataSet.getRowData(calcCtx.row, fncKey);
                    // calc max and set key
                    let fopts: any = {bPositiveOnly: !!bPositiveOnly};
                    DataSetUtils.Metric_setMax(dataSet, metricId, fncKey, applyRows, fopts)
                    calcCtx[fncKey] = true
                    return dataSet.getRowData(calcCtx.row, fncKey);
                }
                fncScope.VisualAgg = (metricId: string): number => {
                    let fncKey = `VizAgg-${metricId || '-'}`;
                    if(fncKey in calcCtx)
                        return dataSet.getRowData(calcCtx.row, fncKey);
                    // calc agg and set key
                    DataSetUtils.Metric_setAggregate(dataSet, metricId, fncKey, applyRows)
                    calcCtx[fncKey] = true
                    return dataSet.getRowData(calcCtx.row, fncKey);
                }
                fncScope.VisualFitY = (metricId_X: string, metricId_Y: string) => {
                    let fncKey = `VizFitY-${metricId_X || '-' || metricId_Y}`;
                    if(fncKey in calcCtx)
                        return dataSet.getRowData(calcCtx.row, fncKey);
                    // calc agg and set key
                    DataSetUtils.Metric_setFitY(dataSet, metricId_X, metricId_Y, fncKey, applyRows)
                    calcCtx[fncKey] = true
                    return dataSet.getRowData(calcCtx.row, fncKey);
                }
                fncScope.FitY = (metricId_X: string, metricId_Y: string) => {
                    let fncKey = `FitY-${metricId_X || '-' || metricId_Y}`;
                    if(fncKey in calcCtx)
                        return dataSet.getRowData(calcCtx.row, fncKey);
                    // calc agg and set key
                    DataSetUtils.Metric_setFitY(dataSet, metricId_X, metricId_Y, fncKey, applyRows)
                    calcCtx[fncKey] = true
                    return dataSet.getRowData(calcCtx.row, fncKey);
                }
            }

            // Evaluate Rows
            for (let row of applyRows) {
                try {
                    // Create row Scope
                    calcCtx.row = row
                    let scope: any = {};
                    _.forEach(self._requiredAttributeIds, (attrId) => {
                        scope[attrId] = dataSet.getRowAttribute(row, attrId);
                    });
                    _.forEach(self._requiredMetricsIds, (metricId) => {
                        scope[metricId] = dataSet.getRowMetric(row, metricId);
                    });

                    _.defaults(scope, fncScope)
                    const metricValue = self._metricFunction.evaluate(scope);
                    dataSet.setRowData(row, self._storeKey, metricValue);
                } catch (e) {
                    self._errorCount++;
                }
            }

        }

        if (bApplyToFilterScope)
            self._setComputedValuesFilteredRows = (async function(dataSet: IMetricDataSet,
                                                                  rowAttributeIds: string[],
                                                                  rowMetricIds: string[],
                                                                  filterRows: IdsRow[],
                                                                  allRows: IdsRow[]): Promise<void>
            {
                await ComputeValues(dataSet, rowAttributeIds, rowMetricIds, filterRows, allRows);
            });
        else
            self._setComputedValuesAllRows = (async function(dataSet: IMetricDataSet,
                                                             rowAttributeIds: string[],
                                                             rowMetricIds: string[],
                                                             allRows: IdsRow[]): Promise<void>
            {
                await ComputeValues(dataSet, rowAttributeIds, rowMetricIds, [], allRows);
            });

    }


}
